/**
 * AuroraCard - Floating glass-morphism card component
 * Aurora-style design with backdrop blur and smooth animations
 */

import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface AuroraCardProps {
  title?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
  className?: string;
}

export const AuroraCard: React.FC<AuroraCardProps> = ({
  title,
  collapsed = false,
  onToggle,
  children,
  className = '',
}) => {
  return (
    <div
      className={`
        bg-white backdrop-blur-md
        border-2 border-gray-300
        rounded-xl shadow-2xl
        transition-all duration-300 ease-in-out
        ${className}
      `}
    >
      {title && (
        <div
          className={`
            flex items-center justify-between
            px-4 py-3
            border-b-2 border-gray-200
            cursor-pointer
            hover:bg-gray-50
            transition-colors
          `}
          onClick={onToggle}
        >
          <h3 className="text-sm font-semibold text-black">
            {title}
          </h3>
          {onToggle && (
            <button className="text-gray-600 hover:text-black transition-colors">
              {collapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      )}

      <div
        className={`
          transition-all duration-300 ease-in-out overflow-hidden
          ${collapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}
        `}
      >
        {children}
      </div>
    </div>
  );
};

interface AuroraCardSectionProps {
  children: React.ReactNode;
  className?: string;
}

export const AuroraCardSection: React.FC<AuroraCardSectionProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`px-4 py-3 ${className}`}>
      {children}
    </div>
  );
};

interface AuroraButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AuroraButton: React.FC<AuroraButtonProps> = ({
  children,
  onClick,
  active = false,
  disabled = false,
  variant = 'secondary',
  size = 'md',
  className = '',
}) => {
  const baseClasses = 'rounded-lg font-medium transition-all duration-200';

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const variantClasses = {
    primary: active
      ? 'bg-[#EBFD03] text-black hover:bg-yellow-400'
      : 'bg-gray-700 text-white hover:bg-gray-800',
    secondary: active
      ? 'bg-gray-200 text-black border-2 border-gray-300'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseClasses}
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      {children}
    </button>
  );
};

interface AuroraToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export const AuroraToggle: React.FC<AuroraToggleProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
}) => {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-gray-700 group-hover:text-black transition-colors font-medium">
        {label}
      </span>
      <div
        className={`
          relative w-11 h-6 rounded-full transition-colors duration-200
          ${checked ? 'bg-[#EBFD03]' : 'bg-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={() => !disabled && onChange(!checked)}
      >
        <div
          className={`
            absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm
            transition-transform duration-200
            ${checked ? 'transform translate-x-5' : ''}
          `}
        />
      </div>
    </label>
  );
};
